(() => {
  const init = () => {
    /* master-all-products previously depended on the legacy showroom root.
       The current Fifth Collective template uses [data-fcp-root], so fall back
       to that root to keep the main product-gallery enhancements active. */
    const root = document.querySelector('[data-ccmp-root]') || document.querySelector('[data-fcp-root]');
    if (!root || root.dataset.ccmpReady === 'true') return;

    root.dataset.ccmpReady = 'true';
    document.body.classList.add('cc-master-product-page');

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const finePointerQuery = window.matchMedia('(hover: hover) and (pointer: fine)');
    const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

    const revealItems = root.querySelectorAll('[data-ccmp-reveal]');
    if (reduced || !('IntersectionObserver' in window)) {
      revealItems.forEach((el) => el.classList.add('is-visible'));
    } else {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        });
      }, { threshold: 0.08, rootMargin: '0px 0px -4% 0px' });
      revealItems.forEach((el) => observer.observe(el));
    }

    const bindDesktopDrag = (rail, draggingClass) => {
      let down = false;
      let startX = 0;
      let startScroll = 0;
      let moved = false;

      rail.addEventListener('pointerdown', (event) => {
        if (event.pointerType === 'touch') return;
        down = true;
        moved = false;
        startX = event.clientX;
        startScroll = rail.scrollLeft;
        rail.classList.add(draggingClass);
        rail.setPointerCapture?.(event.pointerId);
      });

      rail.addEventListener('pointermove', (event) => {
        if (!down) return;
        const delta = event.clientX - startX;
        if (Math.abs(delta) > 4) moved = true;
        rail.scrollLeft = startScroll - delta;
      });

      const release = (event) => {
        if (!down) return;
        down = false;
        rail.releasePointerCapture?.(event?.pointerId);
        window.setTimeout(() => {
          rail.classList.remove(draggingClass);
          const cards = Array.from(rail.querySelectorAll('[data-ccmp-card], .product__media-item:not(.hidden)'));
          if (!moved || !cards.length) return;
          const index = getClosestCard(rail, cards);
          const card = cards[index];
          const target = card.offsetLeft - Math.max(0, (rail.clientWidth - card.offsetWidth) / 2);
          rail.scrollTo({ left: target, behavior: reduced ? 'auto' : 'smooth' });
        }, 28);
      };

      rail.addEventListener('pointerup', release);
      rail.addEventListener('pointercancel', release);
      rail.addEventListener('pointerleave', release);
      rail.addEventListener('click', (event) => {
        if (!moved) return;
        event.preventDefault();
        event.stopPropagation();
        moved = false;
      }, true);
    };

    const getClosestCard = (rail, cards) => {
      if (!cards.length) return 0;
      const center = rail.scrollLeft + rail.clientWidth * 0.5;
      let closest = 0;
      let distance = Infinity;

      cards.forEach((card, index) => {
        const cardCenter = card.offsetLeft + card.offsetWidth * 0.5;
        const currentDistance = Math.abs(cardCenter - center);
        if (currentDistance < distance) {
          distance = currentDistance;
          closest = index;
        }
      });

      return closest;
    };

    // Intentionally no per-frame card transforms. Native scroll physics are smoother
    // on iOS/Android when JavaScript does not continuously rewrite transforms.
    const paintRailCards = () => {};

    const updateRail = (rail) => {
      const cards = Array.from(rail.querySelectorAll('[data-ccmp-card]'));
      if (!cards.length) return;

      const closest = getClosestCard(rail, cards);
      paintRailCards(rail, cards, false);
      cards.forEach((card, index) => card.classList.toggle('ccmp-card-is-active', index === closest));

      const meta = rail.nextElementSibling;
      if (!meta || !meta.classList.contains('ccmp__rail-meta')) return;

      const progress = meta.querySelector('[data-ccmp-rail-progress]');
      const count = meta.querySelector('[data-ccmp-count]');
      if (progress) progress.style.transform = `scaleX(${Math.max(0.1, (closest + 1) / cards.length)})`;
      if (count) count.textContent = String(closest + 1).padStart(2, '0');
    };

    root.querySelectorAll('[data-ccmp-rail]').forEach((rail) => {
      let frame = 0;
      let activeIndex = -1;

      const render = () => {
        frame = 0;
        const cards = Array.from(rail.querySelectorAll('[data-ccmp-card]'));
        if (!cards.length) return;

        const closest = getClosestCard(rail, cards);

        if (closest !== activeIndex) {
          if (activeIndex >= 0) cards[activeIndex]?.classList.remove('ccmp-card-is-active');
          cards[closest]?.classList.add('ccmp-card-is-active');
          activeIndex = closest;
        }

        const meta = rail.nextElementSibling;
        if (!meta || !meta.classList.contains('ccmp__rail-meta')) return;

        const progress = meta.querySelector('[data-ccmp-rail-progress]');
        const count = meta.querySelector('[data-ccmp-count]');
        if (progress) progress.style.transform = `scaleX(${Math.max(0.1, (closest + 1) / cards.length)})`;
        if (count && count.textContent !== String(closest + 1).padStart(2, '0')) {
          count.textContent = String(closest + 1).padStart(2, '0');
        }
      };

      const scheduleRender = () => {
        if (frame) return;
        frame = requestAnimationFrame(render);
      };

      render();
      rail.addEventListener('scroll', scheduleRender, { passive: true });
      rail.addEventListener('scrollend', render, { passive: true });
      if ('ResizeObserver' in window) {
        const railResizeObserver = new ResizeObserver(scheduleRender);
        railResizeObserver.observe(rail);
      } else {
        window.addEventListener('resize', scheduleRender, { passive: true });
      }
      bindDesktopDrag(rail, 'is-dragging');

      rail.addEventListener('wheel', (event) => {
        if (!finePointerQuery.matches) return;
        if (Math.abs(event.deltaY) <= Math.abs(event.deltaX) || Math.abs(event.deltaY) <= 6) return;
        event.preventDefault();
        rail.scrollLeft += event.deltaY * 0.72;
        scheduleRender();
      }, { passive: false });
    });

    /* Cart-silk accordion controller.
       This owns the summary tap in capture phase so the theme's native details
       behavior cannot run a second, competing motion. The panel performs one
       measured height transition; the arrow follows the same cart easing. */
    const initProductAccordions = () => {
      document.querySelectorAll('body.cc-master-product-page .product__accordion').forEach((accordion) => {
        if (accordion.dataset.ccmpAccordionReady === 'true') return;

        const details = accordion.querySelector('.accordion__details');
        const summary = details?.querySelector('.accordion__summary');
        const wrapper = accordion.querySelector('.accordion__content-wrapper');
        if (!details || !summary || !wrapper) return;

        accordion.dataset.ccmpAccordionReady = 'true';
        let settleTimer = 0;

        const commit = (open) => {
          details.open = open;
          summary.setAttribute('aria-expanded', open ? 'true' : 'false');
          wrapper.setAttribute('aria-hidden', open ? 'false' : 'true');
        };

        commit(details.open);
        wrapper.style.setProperty('height', details.open ? 'auto' : '0px', 'important');

        const toggle = (event) => {
          event.preventDefault();
          event.stopPropagation();
          if (event.stopImmediatePropagation) event.stopImmediatePropagation();

          clearTimeout(settleTimer);

          const opening = !details.open;
          const startHeight = wrapper.getBoundingClientRect().height;

          accordion.classList.toggle('ccmp-accordion-opening', opening);
          accordion.classList.toggle('ccmp-accordion-closing', !opening);

          wrapper.style.setProperty('transition', 'none', 'important');
          wrapper.style.setProperty('height', `${startHeight}px`, 'important');
          wrapper.getBoundingClientRect();

          commit(opening);

          if (reduced) {
            wrapper.style.setProperty('height', opening ? 'auto' : '0px', 'important');
            accordion.classList.remove('ccmp-accordion-opening', 'ccmp-accordion-closing');
            return;
          }

          const duration = opening ? 430 : 330;
          const easing = opening ? 'cubic-bezier(.16,1,.30,1)' : 'cubic-bezier(.40,0,.24,1)';
          const endHeight = opening ? wrapper.scrollHeight : 0;

          wrapper.style.setProperty('transition', `height ${duration}ms ${easing}`, 'important');

          requestAnimationFrame(() => {
            wrapper.style.setProperty('height', `${endHeight}px`, 'important');
          });

          settleTimer = window.setTimeout(() => {
            wrapper.style.setProperty('transition', 'none', 'important');
            wrapper.style.setProperty('height', opening ? 'auto' : '0px', 'important');
            accordion.classList.remove('ccmp-accordion-opening', 'ccmp-accordion-closing');
          }, duration + 35);
        };

        summary.addEventListener('click', toggle, true);
      });
    };

    initProductAccordions();

    const initMainGallery = () => {
      const mainProduct = document.querySelector('[id^="MainProduct-"]');
      const gallery = mainProduct?.querySelector('.product__media-wrapper media-gallery');
      const rail = gallery?.querySelector('.product__media-list');
      if (!gallery || !rail || gallery.dataset.ccmpGalleryReady === 'true') return;

      gallery.dataset.ccmpGalleryReady = 'true';


      /* Main-image zoom stays inside the page. No lightbox and no zoom button.
         Native pinch/page zoom remains available; a clean tap toggles an
         additional inline close-up centred around the tap position. */
      let zoomPointerStart = null;
      let zoomMoved = false;

      const resetInlineZoom = (except = null) => {
        rail.querySelectorAll('.product__media-item.ccmp-inline-zoomed').forEach((slide) => {
          if (slide === except) return;
          slide.classList.remove('ccmp-inline-zoomed');
          slide.style.removeProperty('--ccmp-zoom-x');
          slide.style.removeProperty('--ccmp-zoom-y');
        });
      };

      rail.addEventListener('pointerdown', (event) => {
        const slide = event.target.closest('.product__media-item');
        const image = event.target.closest('.product__media img');
        if (!slide || !image) return;
        zoomPointerStart = { x: event.clientX, y: event.clientY, slide, image };
        zoomMoved = false;
      }, { passive: true });

      rail.addEventListener('pointermove', (event) => {
        if (!zoomPointerStart) return;
        if (Math.hypot(event.clientX - zoomPointerStart.x, event.clientY - zoomPointerStart.y) > 8) {
          zoomMoved = true;
        }
      }, { passive: true });

      rail.addEventListener('pointerup', (event) => {
        if (!zoomPointerStart) return;
        const start = zoomPointerStart;
        zoomPointerStart = null;
        if (zoomMoved || start.slide !== event.target.closest('.product__media-item')) return;

        const media = start.slide.querySelector('.product__media');
        const image = start.slide.querySelector('.product__media img');
        if (!media || !image) return;

        const rect = media.getBoundingClientRect();
        const x = Math.max(0, Math.min(100, ((event.clientX - rect.left) / rect.width) * 100));
        const y = Math.max(0, Math.min(100, ((event.clientY - rect.top) / rect.height) * 100));
        const opening = !start.slide.classList.contains('ccmp-inline-zoomed');

        resetInlineZoom(opening ? start.slide : null);
        start.slide.style.setProperty('--ccmp-zoom-x', `${x.toFixed(1)}%`);
        start.slide.style.setProperty('--ccmp-zoom-y', `${y.toFixed(1)}%`);
        start.slide.classList.toggle('ccmp-inline-zoomed', opening);
      }, { passive: true });

      rail.addEventListener('pointercancel', () => {
        zoomPointerStart = null;
        zoomMoved = false;
      }, { passive: true });

      /* Block the theme's product media modal for image taps in this template.
         Video/model controls keep their original behavior. */
      gallery.addEventListener('click', (event) => {
        const slide = event.target.closest('.product__media-item');
        if (!slide || !slide.querySelector('.product__media img')) return;
        if (!event.target.closest('modal-opener, .product__media, .product__media-toggle')) return;
        event.preventDefault();
        event.stopPropagation();
        if (event.stopImmediatePropagation) event.stopImmediatePropagation();
      }, true);

      const ui = document.createElement('div');
      ui.className = 'ccmp-main-gallery-ui';
      ui.setAttribute('aria-hidden', 'true');
      ui.innerHTML = '<span class="ccmp-main-gallery-ui__label">SWIPE THE PIECE</span><span class="ccmp-main-gallery-ui__track"><span></span><i></i></span><span class="ccmp-main-gallery-ui__count"><b>01</b> / <span>01</span></span>';
      gallery.appendChild(ui);

      const currentEl = ui.querySelector('b');
      const totalEl = ui.querySelector('.ccmp-main-gallery-ui__count > span');
      const progressEl = ui.querySelector('.ccmp-main-gallery-ui__track > span');
      const glowEl = ui.querySelector('.ccmp-main-gallery-ui__track > i');

      let activeIndex = -1;
      let galleryFrame = 0;
      let previousVisualProgress = 0;
      let glowIdleTimer = 0;

      const update = () => {
        galleryFrame = 0;
        const slides = Array.from(rail.querySelectorAll('.product__media-item:not(.hidden)'));
        if (!slides.length) return;

        const closest = getClosestCard(rail, slides);

        if (closest !== activeIndex) {
          if (activeIndex >= 0) slides[activeIndex]?.classList.remove('ccmp-main-is-active');
          slides[closest]?.classList.add('ccmp-main-is-active');
          activeIndex = closest;
        }

        const current = String(closest + 1).padStart(2, '0');
        const total = String(slides.length).padStart(2, '0');
        if (currentEl.textContent !== current) currentEl.textContent = current;
        if (totalEl.textContent !== total) totalEl.textContent = total;

        const maxScroll = Math.max(rail.scrollWidth - rail.clientWidth, 1);
        const rawProgress = maxScroll > 1 ? rail.scrollLeft / maxScroll : 1;
        const fluidProgress = slides.length > 1
          ? (((slides.length - 1) * Math.max(0, Math.min(1, rawProgress))) + 1) / slides.length
          : 1;

        const visualProgress = Math.max(1 / Math.max(slides.length, 1), Math.min(1, fluidProgress));
        const progressDelta = Math.abs(visualProgress - previousVisualProgress);
        const motionBoost = Math.min(1, progressDelta * Math.max(slides.length, 1) * 11.5);

        progressEl.style.transform = `scaleX(${visualProgress})`;

        if (glowEl) {
          glowEl.style.left = `${(visualProgress * 100).toFixed(3)}%`;
          glowEl.style.setProperty('--ccmp-glow-boost', motionBoost.toFixed(3));
          glowEl.style.setProperty('--ccmp-glow-stretch', (1 + motionBoost * 0.34).toFixed(3));
          glowEl.style.setProperty('--ccmp-glow-brightness', (1 + motionBoost * 0.24).toFixed(3));
          if (glowIdleTimer) window.clearTimeout(glowIdleTimer);
          glowIdleTimer = window.setTimeout(() => {
            glowEl.style.setProperty('--ccmp-glow-boost', '0');
            glowEl.style.setProperty('--ccmp-glow-stretch', '1');
            glowEl.style.setProperty('--ccmp-glow-brightness', '1');
          }, 120);
        }

        previousVisualProgress = visualProgress;
      };

      const scheduleGalleryUpdate = () => {
        if (galleryFrame) return;
        galleryFrame = requestAnimationFrame(update);
      };

      update();
      rail.addEventListener('scroll', scheduleGalleryUpdate, { passive: true });
      rail.addEventListener('scroll', () => resetInlineZoom(), { passive: true });
      rail.addEventListener('scrollend', update, { passive: true });

      bindDesktopDrag(rail, 'ccmp-is-dragging');

      rail.addEventListener('wheel', (event) => {
        if (!finePointerQuery.matches) return;
        if (Math.abs(event.deltaY) <= Math.abs(event.deltaX) || Math.abs(event.deltaY) <= 6) return;
        event.preventDefault();
        rail.scrollLeft += event.deltaY * 0.72;
        scheduleGalleryUpdate();
      }, { passive: false });

      const mutationObserver = new MutationObserver(scheduleGalleryUpdate);
      mutationObserver.observe(rail, { subtree: false, childList: true, attributes: true, attributeFilter: ['class'] });
      if ('ResizeObserver' in window) {
        const galleryResizeObserver = new ResizeObserver(scheduleGalleryUpdate);
        galleryResizeObserver.observe(rail);
      } else {
        window.addEventListener('resize', scheduleGalleryUpdate, { passive: true });
      }
    };

    initMainGallery();

    const buyTarget = document.querySelector('.product__info-wrapper') || document.querySelector('.product__info-container');
    root.querySelectorAll('[data-ccmp-buy-jump]').forEach((button) => {
      button.addEventListener('click', () => {
        if (!buyTarget) return;
        buyTarget.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'center' });
      });
    });

    root.querySelectorAll('[data-ccmp-magnetic]').forEach((element) => {
      if (reduced || !finePointerQuery.matches) return;
      element.addEventListener('pointermove', (event) => {
        const rect = element.getBoundingClientRect();
        const x = ((event.clientX - rect.left) / rect.width - 0.5) * 7;
        const y = ((event.clientY - rect.top) / rect.height - 0.5) * 5;
        element.style.setProperty('--ccmp-mx', `${x.toFixed(2)}px`);
        element.style.setProperty('--ccmp-my', `${y.toFixed(2)}px`);
      });
      element.addEventListener('pointerleave', () => {
        element.style.setProperty('--ccmp-mx', '0px');
        element.style.setProperty('--ccmp-my', '0px');
      });
    });

    root.querySelectorAll('[data-ccmp-tilt],[data-ccmp-tile]').forEach((element) => {
      if (reduced || !finePointerQuery.matches) return;
      element.addEventListener('pointermove', (event) => {
        const rect = element.getBoundingClientRect();
        const x = clamp((event.clientX - rect.left) / rect.width - 0.5, -0.5, 0.5);
        const y = clamp((event.clientY - rect.top) / rect.height - 0.5, -0.5, 0.5);
        if (element.hasAttribute('data-ccmp-tilt')) {
          element.style.setProperty('--ccmp-tilt-y', `${(x * 2.2).toFixed(2)}deg`);
          element.style.setProperty('--ccmp-tilt-x', `${(-y * 1.8).toFixed(2)}deg`);
        } else {
          element.style.setProperty('--ccmp-tile-y', `${(x * 1.4).toFixed(2)}deg`);
          element.style.setProperty('--ccmp-tile-x', `${(-y * 1.2).toFixed(2)}deg`);
        }
      });
      element.addEventListener('pointerleave', () => {
        element.style.setProperty('--ccmp-tilt-y', '0deg');
        element.style.setProperty('--ccmp-tilt-x', '0deg');
        element.style.setProperty('--ccmp-tile-y', '0deg');
        element.style.setProperty('--ccmp-tile-x', '0deg');
      });
    });

    const tactile = root.querySelectorAll('button,a.ccmp__text-action,.ccmp__product-card');
    tactile.forEach((element) => {
      element.addEventListener('pointerdown', () => element.classList.add('ccmp-is-pressed'));
      ['pointerup','pointercancel','pointerleave'].forEach((name) => {
        element.addEventListener(name, () => window.setTimeout(() => element.classList.remove('ccmp-is-pressed'), 70));
      });
    });

    const pageProgress = root.querySelector('[data-ccmp-page-progress]');
    const rooms = Array.from(root.querySelectorAll('[data-ccmp-room]'));
    let pageTicking = false;

    const getViewportHeight = () => Math.max(1, window.visualViewport?.height || window.innerHeight || document.documentElement.clientHeight);

    const updatePageMotion = () => {
      pageTicking = false;
      const viewportHeight = getViewportHeight();
      const rootRect = root.getBoundingClientRect();
      const total = Math.max(1, root.offsetHeight - viewportHeight);
      const travelled = Math.min(total, Math.max(0, -rootRect.top));
      if (pageProgress) pageProgress.style.transform = `scaleX(${travelled / total})`;

      if (!reduced) {
        const viewportCenter = viewportHeight / 2;
        rooms.forEach((room) => {
          const rect = room.getBoundingClientRect();
          const roomCenter = rect.top + rect.height / 2;
          const delta = clamp((roomCenter - viewportCenter) / viewportHeight, -1, 1);
          room.style.setProperty('--ccmp-room-shift', `${(delta * -18).toFixed(2)}px`);
        });
      }
    };

    const schedulePageMotion = () => {
      if (pageTicking) return;
      pageTicking = true;
      requestAnimationFrame(updatePageMotion);
    };

    updatePageMotion();
    window.addEventListener('scroll', schedulePageMotion, { passive: true });
    window.addEventListener('resize', schedulePageMotion, { passive: true });
    if (window.visualViewport) window.visualViewport.addEventListener('resize', schedulePageMotion, { passive: true });
    if ('ResizeObserver' in window) {
      const pageResizeObserver = new ResizeObserver(schedulePageMotion);
      pageResizeObserver.observe(root);
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);
})();
